// ScheduledJobsManager.jsx - Reusable component for managing scheduled video jobs
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ScheduledJobsManager = ({ userId, buttonColor = '#3b82f6', onJobUpdate = null }) => {
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [dismissedJobIds, setDismissedJobIds] = useState(new Set());
  const [showScheduledJobs, setShowScheduledJobs] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [editScheduledDate, setEditScheduledDate] = useState('');
  const [editScheduledTime, setEditScheduledTime] = useState('');
  const [isUpdatingJob, setIsUpdatingJob] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchScheduledJobs();
      // Refresh every 30 seconds
      const interval = setInterval(fetchScheduledJobs, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  // Load dismissed job IDs from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(`dismissedJobs_${userId}`);
    if (stored) {
      try {
        setDismissedJobIds(new Set(JSON.parse(stored)));
      } catch (e) {
        console.error('Error loading dismissed jobs:', e);
      }
    }
  }, [userId]);

  const fetchScheduledJobs = async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      const response = await axios.get(`/api/language/scheduled-jobs/${userId}`);
      
      if (response.data.success) {
        // Filter pending jobs that haven't passed their scheduled time
        const pendingJobs = response.data.jobs.filter(job => 
          job.status === 'pending' && new Date(job.scheduledDateTime) > new Date()
        );
        setScheduledJobs(pendingJobs);
        
        // Filter completed/failed/cancelled jobs (last 20)
        const executedJobs = response.data.jobs
          .filter(job => ['completed', 'failed', 'cancelled'].includes(job.status))
          .sort((a, b) => new Date(b.executedAt || b.updatedAt) - new Date(a.executedAt || a.updatedAt))
          .slice(0, 20);
        setCompletedJobs(executedJobs);
      } else {
        setError(response.data.message || 'Failed to fetch scheduled jobs');
      }
    } catch (error) {
      console.error('Error fetching scheduled jobs:', error);
      
      let errorMessage = 'Failed to load scheduled jobs';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Scheduled jobs endpoint not found';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while fetching scheduled jobs';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 3000);
  };

  const handleEditJob = (job) => {
    const scheduledDate = new Date(job.scheduledDateTime);
    // Use local timezone for both date and time to avoid mismatch
    const year = scheduledDate.getFullYear();
    const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
    const day = String(scheduledDate.getDate()).padStart(2, '0');
    const hours = String(scheduledDate.getHours()).padStart(2, '0');
    const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
    
    setEditingJob(job);
    setEditScheduledDate(`${year}-${month}-${day}`);
    setEditScheduledTime(`${hours}:${minutes}`);
  };

  const handleUpdateJob = async () => {
    if (!editScheduledDate || !editScheduledTime) {
      showNotification('error', 'Please select both date and time');
      return;
    }

    setIsUpdatingJob(true);
    
    try {
      const scheduledDateTime = new Date(`${editScheduledDate}T${editScheduledTime}`);
      
      // Validate the date is in the future
      if (scheduledDateTime <= new Date()) {
        showNotification('error', 'Scheduled time must be in the future');
        setIsUpdatingJob(false);
        return;
      }
      
      const response = await axios.put(`/api/language/scheduled-jobs/${editingJob._id}`, {
        scheduledDateTime: scheduledDateTime.toISOString()
      });

      if (response.data.success) {
        showNotification('success', `Scheduled time updated to ${scheduledDateTime.toLocaleString()}`);
        fetchScheduledJobs();
        setEditingJob(null);
        setEditScheduledDate('');
        setEditScheduledTime('');
        
        // Notify parent component if callback provided
        if (onJobUpdate) {
          onJobUpdate();
        }
      } else {
        showNotification('error', response.data.message || 'Failed to update scheduled job');
      }
    } catch (error) {
      console.error('Error updating scheduled job:', error);
      
      let errorMessage = 'Error updating scheduled job';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Scheduled job not found';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid date/time provided';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      showNotification('error', errorMessage);
    } finally {
      setIsUpdatingJob(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled video generation?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/language/scheduled-jobs/${jobId}`);

      if (response.data.success) {
        showNotification('success', 'Scheduled job cancelled successfully');
        fetchScheduledJobs();
        
        // Notify parent component if callback provided
        if (onJobUpdate) {
          onJobUpdate();
        }
      } else {
        showNotification('error', response.data.message || 'Failed to cancel scheduled job');
      }
    } catch (error) {
      console.error('Error cancelling scheduled job:', error);
      
      let errorMessage = 'Error cancelling scheduled job';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Scheduled job not found. It may have already been cancelled or completed.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to cancel this job.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while cancelling job. Please try again.';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      showNotification('error', errorMessage);
    }
  };

  const handleDismissJob = (jobId) => {
    const newDismissed = new Set(dismissedJobIds);
    newDismissed.add(jobId);
    setDismissedJobIds(newDismissed);
    
    // Save to localStorage
    localStorage.setItem(`dismissedJobs_${userId}`, JSON.stringify([...newDismissed]));
  };

  const getLanguageName = (code) => {
    const languageNames = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'tr': 'Turkish',
      'nl': 'Dutch',
      'sv': 'Swedish',
      'pl': 'Polish',
      'th': 'Thai',
      'vi': 'Vietnamese',
      'id': 'Indonesian'
    };
    return languageNames[code] || code.toUpperCase();
  };

  const getLanguageFlag = (code) => {
    const flagMap = {
      'en': 'us',
      'es': 'es',
      'fr': 'fr',
      'de': 'de',
      'it': 'it',
      'pt': 'pt',
      'ru': 'ru',
      'zh': 'cn',
      'ja': 'jp',
      'ko': 'kr',
      'ar': 'sa',
      'hi': 'in',
      'tr': 'tr',
      'nl': 'nl',
      'sv': 'se',
      'pl': 'pl',
      'th': 'th',
      'vi': 'vn',
      'id': 'id'
    };
    return flagMap[code] || 'xx';
  };

  if (isLoading) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        textAlign: 'center'
      }}>
        <i className="fas fa-spinner fa-spin" style={{ 
          fontSize: '32px', 
          color: buttonColor,
          marginBottom: '16px'
        }}></i>
        <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
          Loading scheduled jobs...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '25px',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
          borderRadius: '10px',
          padding: '20px',
          border: '2px solid #ff6b6b',
          boxShadow: '0 4px 12px rgba(220, 53, 69, 0.15)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#dc2626',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              !
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#dc2626',
                marginBottom: '6px'
              }}>
                Failed to Load Scheduled Jobs
              </div>
              <div style={{
                fontSize: '14px',
                color: '#721c24',
                lineHeight: '1.6',
                marginBottom: '16px'
              }}>
                {error}
              </div>
              <button
                onClick={fetchScheduledJobs}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <i className="fas fa-sync-alt"></i>
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (scheduledJobs.length === 0 && completedJobs.filter(job => !dismissedJobIds.has(job._id)).length === 0) {
    return null; // Don't show section if no scheduled jobs or undismissed completed jobs
  }

  // Filter out dismissed jobs
  const visibleCompletedJobs = completedJobs.filter(job => !dismissedJobIds.has(job._id));

  return (
    <div>
      {/* Notification Toast */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          backgroundColor: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          <span>{notification.message}</span>
        </div>
      )}

      {/* Pending Scheduled Jobs Section */}
      {scheduledJobs.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: '20px'
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <i className="fas fa-calendar-check" style={{ 
              color: buttonColor, 
              fontSize: '20px' 
            }}></i>
            <h3 style={{
              margin: '0',
              color: '#2d3748',
              fontSize: '18px',
              fontWeight: 600
            }}>
              Scheduled Video Jobs
            </h3>
            <span style={{
              backgroundColor: `${buttonColor}20`,
              color: buttonColor,
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 600
            }}>
              {scheduledJobs.length} pending
            </span>
          </div>
          <button
            onClick={() => setShowScheduledJobs(!showScheduledJobs)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: `2px solid ${buttonColor}`,
              borderRadius: '8px',
              color: buttonColor,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonColor;
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = buttonColor;
            }}
          >
            <i className={`fas fa-chevron-${showScheduledJobs ? 'up' : 'down'}`} style={{ marginRight: '8px' }}></i>
            {showScheduledJobs ? 'Hide' : 'Show'} Jobs
          </button>
        </div>

        {showScheduledJobs && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {scheduledJobs.map((job) => {
              const scheduledDate = new Date(job.scheduledDateTime);
              const isEditing = editingJob?._id === job._id;
              const timeUntil = scheduledDate - new Date();
              const hoursUntil = Math.floor(timeUntil / (1000 * 60 * 60));
              const minutesUntil = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

              return (
                <div
                  key={job._id}
                  style={{
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '10px',
                    border: `2px solid ${isEditing ? buttonColor : '#e2e8f0'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: isEditing ? '15px' : '0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '8px'
                      }}>
                        {job.jobType === 'location' ? (
                          <>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                              height: '32px',
                              backgroundColor: `${buttonColor}15`,
                              borderRadius: '8px',
                              border: `1px solid ${buttonColor}30`
                            }}>
                              <i className="fas fa-map-marker-alt" style={{ fontSize: '16px', color: buttonColor }}></i>
                            </div>
                            <div>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#2d3748',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                {job.locationName || 'Unknown Location'} 
                                <span style={{ fontSize: '11px', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', color: '#4a5568' }}>
                                  {(job.targetLanguage || job.language || 'en').toUpperCase()}
                                </span>
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#718096'
                              }}>
                                Location Video Generation
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <span 
                              className={`fi fi-${getLanguageFlag(job.targetLanguage)}`}
                              style={{ 
                                fontSize: '24px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                              }}
                            ></span>
                            <div>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#2d3748'
                              }}>
                                {getLanguageName(job.targetLanguage)}
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#718096'
                              }}>
                                Language Video Gen
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {!isEditing ? (
                        <>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '12px'
                          }}>
                            <i className="fas fa-clock" style={{ color: buttonColor }}></i>
                            <span style={{
                              fontSize: '14px',
                              color: '#4a5568',
                              fontWeight: 500
                            }}>
                              {scheduledDate.toLocaleString()}
                            </span>
                          </div>
                          <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#718096'
                          }}>
                            <i className="fas fa-hourglass-half" style={{ marginRight: '6px', color: buttonColor }}></i>
                            Starts in {hoursUntil}h {minutesUntil}m
                          </div>
                        </>
                      ) : (
                        <div style={{
                          marginTop: '12px',
                          padding: '15px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                            marginBottom: '12px'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                color: '#4a5568',
                                fontSize: '13px',
                                fontWeight: 600
                              }}>
                                <i className="fas fa-calendar" style={{ marginRight: '6px' }}></i>
                                Date
                              </label>
                              <input
                                type="date"
                                value={editScheduledDate}
                                onChange={(e) => setEditScheduledDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  border: '2px solid #e2e8f0',
                                  fontSize: '13px',
                                  outline: 'none',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                color: '#4a5568',
                                fontSize: '13px',
                                fontWeight: 600
                              }}>
                                <i className="fas fa-clock" style={{ marginRight: '6px' }}></i>
                                Time
                              </label>
                              <input
                                type="time"
                                value={editScheduledTime}
                                onChange={(e) => setEditScheduledTime(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  border: '2px solid #e2e8f0',
                                  fontSize: '13px',
                                  outline: 'none',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                          </div>
                          {editScheduledDate && editScheduledTime && (
                            <div style={{
                              padding: '8px',
                              backgroundColor: `${buttonColor}10`,
                              borderRadius: '6px',
                              fontSize: '12px',
                              color: '#4a5568',
                              textAlign: 'center'
                            }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '6px', color: buttonColor }}></i>
                              New time: <strong>{new Date(`${editScheduledDate}T${editScheduledTime}`).toLocaleString()}</strong>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginLeft: '15px'
                    }}>
                      {!isEditing ? (
                        <>
                          <button
                            onClick={() => handleEditJob(job)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: buttonColor,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                          >
                            <i className="fas fa-edit"></i>
                            Edit
                          </button>
                          <button
                            onClick={() => handleCancelJob(job._id)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            <i className="fas fa-times"></i>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleUpdateJob}
                            disabled={isUpdatingJob || !editScheduledDate || !editScheduledTime}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: buttonColor,
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: (isUpdatingJob || !editScheduledDate || !editScheduledTime) ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              opacity: (isUpdatingJob || !editScheduledDate || !editScheduledTime) ? 0.6 : 1
                            }}
                          >
                            {isUpdatingJob ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check"></i>
                                Save
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setEditingJob(null);
                              setEditScheduledDate('');
                              setEditScheduledTime('');
                            }}
                            disabled={isUpdatingJob}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: isUpdatingJob ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              opacity: isUpdatingJob ? 0.6 : 1
                            }}
                          >
                            <i className="fas fa-times"></i>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Execution Results - Dismissible Alerts */}
      {visibleCompletedJobs.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          {visibleCompletedJobs.map((job) => {
            const executedDate = new Date(job.executedAt || job.updatedAt);
            const scheduledDate = new Date(job.scheduledDateTime);
            const isSuccess = job.status === 'completed';
            const isFailed = job.status === 'failed';
            const isCancelled = job.status === 'cancelled';

            return (
              <div
                key={job._id}
                style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: `2px solid ${isSuccess ? '#86efac' : isFailed ? '#fca5a5' : '#e2e8f0'}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  animation: 'slideInDown 0.4s ease-out'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}>
                  {/* Status Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: isSuccess 
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                      : isFailed 
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    flexShrink: 0
                  }}>
                    <i className={`fas ${isSuccess ? 'fa-check' : isFailed ? 'fa-times' : 'fa-ban'}`}></i>
                  </div>

                  {/* Job Details */}
                  <div style={{ flex: 1 }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: isSuccess ? '#059669' : isFailed ? '#dc2626' : '#4b5563'
                      }}>
                        {isSuccess ? 'Job Completed Successfully' : isFailed ? 'Job Failed' : 'Job Cancelled'}
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        backgroundColor: isSuccess 
                          ? '#dcfce7' 
                          : isFailed 
                          ? '#fee2e2'
                          : '#f3f4f6',
                        color: isSuccess 
                          ? '#166534' 
                          : isFailed 
                          ? '#991b1b'
                          : '#374151'
                      }}>
                        {job.status}
                      </span>
                    </div>

                    {/* Job Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px'
                    }}>
                      {job.jobType === 'location' ? (
                        <>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            backgroundColor: `${buttonColor}15`,
                            borderRadius: '8px',
                            border: `1px solid ${buttonColor}30`
                          }}>
                            <i className="fas fa-map-marker-alt" style={{ fontSize: '16px', color: buttonColor }}></i>
                          </div>
                          <div>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#2d3748',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              {job.locationName || 'Unknown Location'} 
                              <span style={{ fontSize: '11px', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', color: '#4a5568' }}>
                                {(job.targetLanguage || job.language || 'en').toUpperCase()}
                              </span>
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#718096'
                            }}>
                              Location Video Generation
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <span 
                            className={`fi fi-${getLanguageFlag(job.targetLanguage)}`}
                            style={{ 
                              fontSize: '24px',
                              borderRadius: '4px',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                            }}
                          ></span>
                          <div>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: 600,
                              color: '#2d3748'
                            }}>
                              {getLanguageName(job.targetLanguage)}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#718096'
                            }}>
                              Language Video Generation
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Execution Time */}
                    <div style={{
                      padding: '10px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#4a5568',
                      marginBottom: isFailed && job.error ? '12px' : '0'
                    }}>
                      <i className="fas fa-clock" style={{ marginRight: '6px', color: buttonColor }}></i>
                      Executed at <strong>{executedDate.toLocaleString()}</strong>
                      <span style={{ margin: '0 8px', color: '#cbd5e0' }}>•</span>
                      Scheduled for <strong>{scheduledDate.toLocaleString()}</strong>
                    </div>

                    {/* Error Message (if failed) */}
                    {isFailed && job.error && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '6px',
                        border: '1px solid #fca5a5'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#dc2626',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <i className="fas fa-exclamation-circle"></i>
                          Error Details
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#991b1b',
                          fontFamily: 'monospace',
                          lineHeight: '1.6',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          backgroundColor: 'white',
                          padding: '8px',
                          borderRadius: '4px'
                        }}>
                          {job.error}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dismiss Button */}
                  <button
                    onClick={() => handleDismissJob(job._id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'white',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#718096',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      height: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderColor = '#cbd5e0';
                      e.target.style.color = '#4a5568';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.color = '#718096';
                    }}
                  >
                    <i className="fas fa-times"></i>
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style jsx>{`
        @import url('https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/css/flag-icons.min.css');
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideInDown {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ScheduledJobsManager;
